import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "../../components/alert";
import Footer from "../../components/footer";
import Navbar from "../../components/navbar";
import { resetPassword } from "../../common/services/auth";
import "./styles/signup.css";
import { ChedaGamingForm } from "../../components/form/cheda-gaming-form";
import ChedaGamingPasswordInput from "../../components/form/cheda-gaming-password-input";
import ChedGamingButton from "../../components/cheda-gaming-button";
import * as Yup from "yup";
import { Lock } from "lucide-react";
import { useAuth } from "../../common/store/authStore";

function ResetPassword() {
  const location: any = useLocation();
  const navigate = useNavigate();

  const { setAuthenticatedUser, setAccessToken, setRefreshToken } = useAuth();

  const initialEmail = location.state?.email || "";

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<any | null>(null);

  const showAlert = (type: any, message: string, title: string | null = null) =>
    setAlert({ type, message, title });
  const hideAlert = () => setAlert(null);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
    newPassword: Yup.string().required("New password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm password is required"),
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoading(true);
    try {
      const payload = { email: values.email, newPassword: values.newPassword };
      const res = await resetPassword(payload);

      setAuthenticatedUser(res.user || null);
      setAccessToken(res.accessToken || null);
      setRefreshToken(res.refreshToken || null);

      // TEMP: Skip OTP redirect, always log in
      showAlert(
        "success",
        "Password reset successful. You are now logged in.",
        "Success",
      );
      navigate("/tournaments");
    } catch (error: any) {
      showAlert(
        "error",
        error?.message || "Could not reset password",
        "Reset Failed",
      );
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="signup-page page-with-navbar-footer">
      <Navbar />

      <div className="signup-background">
        <div className="background-overlay"></div>
      </div>

      <div className="signup-container">
        <div className="signup-card">
          <div className="signup-header">
            <div className="logo-container">
              <img
                src="/images/chedagaming_logo.png"
                alt="Cheda Gaming"
                className="signup-logo"
              />
            </div>
            <h1 className="signup-title">Reset Password</h1>
            <p className="signup-subtitle">
              Please enter a new password for your account
            </p>
          </div>

          <ChedaGamingForm
            initialValues={{
              email: initialEmail,
              newPassword: "",
              confirmPassword: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <>
                <ChedaGamingPasswordInput
                  name="newPassword"
                  label="New Password"
                  placeholder="Enter new password"
                  required
                  disabled={loading || formik.isSubmitting}
                  icon={<Lock className="input-icon" size={20} />}
                />

                <ChedaGamingPasswordInput
                  name="confirmPassword"
                  label="Confirm Password"
                  placeholder="Confirm new password"
                  required
                  disabled={loading || formik.isSubmitting}
                  icon={<Lock className="input-icon" size={20} />}
                />

                <div style={{ marginTop: 16 }}>
                  <ChedGamingButton
                    type="submit"
                    loading={loading || formik.isSubmitting}
                    className="signup-button"
                  >
                    Reset Password
                  </ChedGamingButton>
                </div>
              </>
            )}
          </ChedaGamingForm>
        </div>
      </div>

      <Footer />

      {alert && (
        <Alert
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onClose={hideAlert}
          duration={5000}
          position="top-right"
        />
      )}
    </div>
  );
}

export default ResetPassword;
